<template>
  <div class="campanhas-page">
    <div class="page-header">
      <div class="header-top">
        <div>
          <h2 class="page-title mb-25">Campanhas</h2>
          <p class="page-subtitle text-muted mb-0">Gerencie suas campanhas de e-mail com cadência</p>
        </div>
        <div class="header-actions">
          <b-button variant="primary" size="sm" @click="showCreateModal = true">
            <feather-icon icon="PlusIcon" size="14" class="mr-25" />
            Nova Campanha
          </b-button>
        </div>
      </div>
    </div>

    <b-card class="filters-card" no-body>
      <div class="filters-bar">
        <b-row align-v="end">
          <b-col md="3" lg="2">
            <b-form-group label="Status" label-size="sm" class="mb-0">
              <b-form-select v-model="filters.status" size="sm" @change="loadCampaigns">
                <b-form-select-option :value="null">Todos</b-form-select-option>
                <b-form-select-option value="rascunho">Rascunho</b-form-select-option>
                <b-form-select-option value="ativa">Ativa</b-form-select-option>
                <b-form-select-option value="pausada">Pausada</b-form-select-option>
                <b-form-select-option value="concluida">Concluída</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="2" lg="1">
            <b-button variant="outline-secondary" size="sm" @click="loadCampaigns">
              <feather-icon icon="RefreshCwIcon" size="14" />
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-1 text-muted">Carregando campanhas...</p>
    </div>

    <div v-else-if="campaigns.length === 0" class="empty-state text-muted">
      <feather-icon icon="SendIcon" size="48" class="mb-1" />
      <h4>Nenhuma campanha encontrada</h4>
      <p>Crie sua primeira campanha para começar a enviar e-mails.</p>
      <b-button variant="primary" size="sm" @click="showCreateModal = true">
        <feather-icon icon="PlusIcon" size="14" class="mr-25" />
        Nova Campanha
      </b-button>
    </div>

    <div v-else class="campaigns-grid">
      <b-card
        v-for="c in campaigns"
        :key="c.id"
        class="campaign-card mb-0"
        body-class="p-1"
        @click="goToDetail(c.id)"
      >
        <div class="cc-header">
          <div class="cc-title-row">
            <h5 class="cc-name mb-0">{{ c.nome }}</h5>
            <b-badge :variant="statusVariant(c.status)" pill class="cc-status">{{ statusLabel(c.status) }}</b-badge>
          </div>
          <p v-if="c.descricao" class="cc-desc text-muted mb-0">{{ c.descricao }}</p>
        </div>
        <div class="cc-stats">
          <b-badge variant="light-secondary" class="cc-stat">
            <feather-icon icon="UsersIcon" size="14" class="mr-25" />
            <span>{{ c.recipients_count || 0 }} dest.</span>
          </b-badge>
          <b-badge variant="light-secondary" class="cc-stat">
            <feather-icon icon="LayersIcon" size="14" class="mr-25" />
            <span>{{ c.steps_count || 0 }} etapas</span>
          </b-badge>
          <b-badge variant="light-success" class="cc-stat">
            <feather-icon icon="CheckCircleIcon" size="14" class="mr-25" />
            <span>{{ c.sent_count || 0 }} env.</span>
          </b-badge>
          <b-badge v-if="c.opened_count > 0" variant="light-info" class="cc-stat">
            <feather-icon icon="EyeIcon" size="14" class="mr-25" />
            <span>{{ c.opened_count }} abertos</span>
          </b-badge>
          <b-badge v-if="c.failed_count > 0" variant="light-danger" class="cc-stat">
            <feather-icon icon="AlertCircleIcon" size="14" class="mr-25" />
            <span>{{ c.failed_count }} falhas</span>
          </b-badge>
          <b-badge v-if="c.pending_count > 0" variant="light-warning" class="cc-stat">
            <feather-icon icon="ClockIcon" size="14" class="mr-25" />
            <span>{{ c.pending_count }} pend.</span>
          </b-badge>
        </div>
        <div class="cc-footer border-top pt-50">
          <small class="text-muted">Criada em {{ formatDate(c.created_at) }}</small>
          <feather-icon icon="ChevronRightIcon" size="16" class="text-muted" />
        </div>
      </b-card>
    </div>

    <div class="table-footer" v-if="totalRows > 0">
      <small class="text-muted">{{ totalRows }} campanha(s)</small>
      <b-pagination
        v-if="totalRows > perPage"
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        size="sm"
        class="mb-0"
        @change="onPageChange"
      />
    </div>

    <b-modal
      v-model="showCreateModal"
      title="Nova Campanha"
      ok-title="Criar"
      cancel-title="Cancelar"
      @ok="createCampaign"
      :ok-disabled="!newCampaign.nome"
      centered
      no-close-on-backdrop
    >
      <b-form-group label="Nome da campanha" label-for="camp-name">
        <b-form-input id="camp-name" v-model="newCampaign.nome" placeholder="Ex: Lançamento Coleção Verão" />
      </b-form-group>
      <b-form-group label="Descrição (opcional)" label-for="camp-desc">
        <b-form-textarea id="camp-desc" v-model="newCampaign.descricao" rows="2" placeholder="Descrição breve da campanha" />
      </b-form-group>
      <b-form-group label="Prefixo do assunto (opcional)" label-for="camp-prefix">
        <b-form-input id="camp-prefix" v-model="newCampaign.subject_prefix" placeholder="Ex: [Hanes] " />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import campaignClient from '@/api/CRM/campaign.client';
import {
  BCard, BButton, BBadge, BSpinner, BRow, BCol, BFormGroup,
  BFormSelect, BFormSelectOption, BPagination, BModal, BFormInput, BFormTextarea,
} from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'CampanhasList',
  components: {
    BCard, BButton, BBadge, BSpinner, BRow, BCol, BFormGroup,
    BFormSelect, BFormSelectOption, BPagination, BModal, BFormInput, BFormTextarea,
  },
  data() {
    return {
      loading: false,
      campaigns: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      filters: { status: null },
      showCreateModal: false,
      newCampaign: { nome: '', descricao: '', subject_prefix: '' },
    };
  },
  created() {
    this.loadCampaigns();
  },
  methods: {
    async loadCampaigns() {
      this.loading = true;
      try {
        const params = { page: this.currentPage, limit: this.perPage };
        if (this.filters.status) params.status = this.filters.status;
        const result = await campaignClient.list(params);
        const payload = result.data || result;
        this.campaigns = payload.items || [];
        this.totalRows = payload.total || this.campaigns.length;
      } catch (error) {
        console.error('Erro ao carregar campanhas:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao carregar campanhas',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async createCampaign(e) {
      e.preventDefault();
      try {
        const result = await campaignClient.create(this.newCampaign);
        const campaign = result.data || result;
        this.showCreateModal = false;
        this.newCampaign = { nome: '', descricao: '', subject_prefix: '' };
        this.$router.push({ name: 'crm-campanha-detail', params: { id: campaign.id } });
      } catch (error) {
        console.error('Erro ao criar campanha:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: error.response?.data?.message || 'Erro ao criar campanha',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    goToDetail(id) {
      this.$router.push({ name: 'crm-campanha-detail', params: { id } });
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadCampaigns();
    },
    statusVariant(s) {
      const map = {
        rascunho: 'light-secondary',
        ativa: 'light-success',
        pausada: 'light-warning',
        concluida: 'light-info',
      };
      return map[s] || 'light-secondary';
    },
    statusLabel(s) {
      const map = { rascunho: 'Rascunho', ativa: 'Ativa', pausada: 'Pausada', concluida: 'Concluída' };
      return map[s] || s;
    },
    formatDate(d) {
      if (!d) return '-';
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.campanhas-page { padding: 0.75rem; }
.page-header { margin-bottom: 1.25rem; }
.header-top { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 1rem; }
.page-title { font-size: 1.5rem; font-weight: 600; }
.page-subtitle { font-size: 0.95rem; }

.filters-card { margin-bottom: 1rem; }
.filters-bar { padding: 1rem 1.25rem; }

.empty-state { text-align: center; padding: 3rem 1rem; }

.campaigns-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 1rem; }
.campaign-card {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.campaign-card:hover {
  transform: translateY(-2px);
}

.cc-header { margin-bottom: 0.75rem; }
.cc-title-row { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-bottom: 0.25rem; }
.cc-name { font-size: 0.95rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cc-status { font-size: 0.7rem; flex-shrink: 0; }
.cc-desc { font-size: 0.8rem; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.cc-stats { display: flex; flex-wrap: wrap; gap: 0.375rem; margin-bottom: 0.75rem; }
.cc-stat {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 500;
}

.cc-footer { display: flex; justify-content: space-between; align-items: center; }

.table-footer { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 0; margin-top: 0.5rem; }

@media (max-width: 768px) {
  .campaigns-grid { grid-template-columns: 1fr; }
}
</style>
